/**
 * Share Utilities
 * Reusable functions for sharing content via WhatsApp and system share API
 */

/**
 * Opens WhatsApp to share a message
 * @param {string} message - Message to share
 * @param {string|null} phoneNumber - Optional phone number (with country code, digits only)
 * @example
 * // With phone number
 * shareViaWhatsApp('Hello!', '573001234567');
 * 
 * // Without phone number (lets user choose contact)
 * shareViaWhatsApp('Hello!');
 */
export const shareViaWhatsApp = (message, phoneNumber = null) => {
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = phoneNumber
        ? `https://wa.me/${phoneNumber}?text=${encodedMessage}`
        : `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
};

/**
 * Uses the Web Share API on mobile or falls back to copying to clipboard on desktop
 * @param {Object} shareData - Data to share
 * @param {string} shareData.title - Title for the share dialog
 * @param {string} shareData.text - Text content to share
 * @param {Function} onSuccess - Callback for successful share/copy
 * @param {Function} onError - Callback for error (optional)
 * @example
 * shareViaSystem(
 *   { title: 'Invoice', text: 'Check out invoice #123' },
 *   () => toast.success('Shared successfully'),
 *   (error) => toast.error('Failed to share')
 * );
 */
export const shareViaSystem = async (shareData, onSuccess, onError) => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
        // Use native share dialog on mobile
        try {
            await navigator.share(shareData);
            if (onSuccess) onSuccess();
        } catch (error) {
            // User cancelled the share dialog (AbortError) - don't show error
            if (error.name !== 'AbortError') {
                if (onError) onError(error);
            }
        }
    } else {
        // Fallback: copy to clipboard on desktop
        try {
            await navigator.clipboard.writeText(shareData.text);
            if (onSuccess) onSuccess();
        } catch (error) {
            if (onError) onError(error);
        }
    }
};
